import {
  FirebaseAnalytics,
} from '@capacitor-community/firebase-analytics';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

import ENVIRONMENT from '@/environments/environment';
import {
  CheckoutAnalyticsProps,
  ClarityTaxonomyProps,
  ClientTaxonomyEvents,
  ClientTaxonomyProps,
  EntryContinueAnalytics,
  GenerateLeadAnalytics,
  LaborDataContinueAnalytics,
  NfcSkipAnalytics,
  NfcSuccessAnalytics,
  PaymentContinueAnalytics,
  PersonalDataContinueAnalytics,
  PurchaseAnalytics,
  ShippingContinueAnalytics,
  ShowNotificationBannerAnalytics,
  SignatureAnalyticsProps,
  SimulationAnalyticsProps,
  TaxonomyEventGeneralProps,
} from '@/interfaces/AnalyticsInterface/AnalyticsInterface';

import { setClarityCustomTags } from './clarity';
import InsightService from './insights';

dayjs.extend(duration);

type AnalyticsJob = {
  type: 'setUserProperties' | 'registerEvent' | 'setScreenName' | 'setUserId';
  payload: Record<string, unknown>;
};

class AnalyticsService {
  private static instance: AnalyticsService;

  private queue: AnalyticsJob[] = [];

  private isInitialized = false;

  private initializingFirebase: boolean = false;

  private isProcessing = false;

  private constructor() {}

  static getInstance(): AnalyticsService {
    if (!this.instance) {
      this.instance = new AnalyticsService();
    }
    return this.instance;
  }

  private async processQueue() {
    if (this.isProcessing || !this.isInitialized || this.queue.length === 0) return;

    this.isProcessing = true;
    const job = this.queue.shift();

    try {
      if (!job) return;
      switch (job.type) {
        case 'setUserProperties':
          await AnalyticsService.setUserProperty(
            job.payload as { name: string; value: string },
          );
          break;
        case 'registerEvent':
          await AnalyticsService.logEvent(
            job.payload as { name: string; params: object },
          );
          break;
        default:
          console.warn('Unknown analytics job type:', job.type);
      }
    } catch (error) {
      console.error('Error processing analytics job:', error);
    } finally {
      this.isProcessing = false;
      if (this.queue.length > 0) {
        this.processQueue();
      }
    }
  }

  private static async setUserProperty({
    name,
    value,
  }: {
    name: string;
    value: string;
  }) {
    await FirebaseAnalytics.setUserProperty({ name, value });
  }

  private static async logEvent({
    name,
    params,
  }: {
    name: string;
    params: object;
  }) {
    await FirebaseAnalytics.logEvent({ name, params });
  }

  private enqueue(job: AnalyticsJob) {
    this.queue.push(job);
    if (this.queue.length === 1) {
      this.processQueue();
    }
  }

  public async initialize() {
    if (this.isInitialized || this.initializingFirebase) return;
    this.initializingFirebase = true;

    try {
      await FirebaseAnalytics.initializeFirebase({
        apiKey: ENVIRONMENT.FIREBASE_API_KEY!,
        authDomain: ENVIRONMENT.FIREBASE_AUTH_DOMAIN!,
        projectId: ENVIRONMENT.FIREBASE_PROJECT_ID!,
        storageBucket: ENVIRONMENT.FIREBASE_STORAGE_BUCKET!,
        messagingSenderId: ENVIRONMENT.FIREBASE_MESSAGING_SENDER_ID!,
        appId: ENVIRONMENT.FIREBASE_APP_ID!,
        measurementId: ENVIRONMENT.FIREBASE_MEASUREMENT_ID!,
      });
      await FirebaseAnalytics.setCollectionEnabled({ enabled: true });
    } catch (error: any) {
      if (error.toString().includes('Firebase app already exists')) {
        console.log('Firebase app already initialized');
      } else {
        console.log('Error initializing Firebase Analytics:', error);
      }
    }

    this.isInitialized = true;
    this.processQueue();
  }

  async setAnalyticsUserProperties(
    props: Partial<Record<ClientTaxonomyProps, string>>,
  ) {
    if (!this.isInitialized) await this.initialize();

    Object.entries(props).forEach(([key, value]) => {
      if (value) {
        this.enqueue({
          type: 'setUserProperties',
          payload: { name: key, value: value.toString() },
        });
      }
    });
  }

  async registerAnalyticsEvent(
    props: Partial<Record<ClientTaxonomyEvents, object>>,
  ) {
    if (!this.isInitialized) await this.initialize();

    Object.entries(props).forEach(([key, params]) => {
      this.enqueue({
        type: 'registerEvent',
        payload: { name: key, params: params || {} },
      });
    });
  }
}

export const Analytics = AnalyticsService.getInstance();

// Auxiliary functions
export const getTimeResult = (initTime: dayjs.Dayjs) => {
  const finishTime = dayjs();
  return dayjs.duration(finishTime.diff(initTime)).asSeconds();
};

export const resolveAgeSegment = (birthDate: string) => {
  const customerAge = dayjs().year() - dayjs(birthDate).year();
  // console.log('customerAge', customerAge);
  if (customerAge < 26) {
    return '18-25';
  }
  if (customerAge >= 26 && customerAge < 36) {
    return '26-35';
  }
  if (customerAge >= 36 && customerAge < 46) {
    return '36-45';
  }
  if (customerAge >= 46 && customerAge < 56) {
    return '46-55';
  }
  if (customerAge >= 56 && customerAge < 66) {
    return '56-65';
  }
  if (customerAge >= 66 && customerAge < 76) {
    return '66-75';
  }
  return '75+';
};

// Eventos generales
export const registerExitTry = (
  generalData: TaxonomyEventGeneralProps,
  stepNumber: string,
) => {
  const { origin, uuid } = generalData;
  Analytics.registerAnalyticsEvent({
    [ClientTaxonomyEvents.TRY_EXIT]: {
      origin: origin || '',
      credit_proposal_id: uuid || '',
      step_number: stepNumber || '',
    },
  });
};

export const registerExitBack = (
  generalData: TaxonomyEventGeneralProps,
  stepNumber: string,
) => {
  const { origin, uuid } = generalData;
  Analytics.registerAnalyticsEvent({
    [ClientTaxonomyEvents.BACK_EXIT]: {
      origin: origin || '',
      credit_proposal_id: uuid || '',
      step_number: stepNumber || '',
    },
  });
};

export const registerEventKO = (
  generalData: TaxonomyEventGeneralProps,
  stepNumber: string,
) => {
  const { origin, uuid } = generalData;
  Analytics.registerAnalyticsEvent({
    [ClientTaxonomyEvents.EVENT_KO]: {
      origin: origin || '',
      credit_proposal_id: uuid || '',
      step_number: stepNumber || '',
    },
  });
  InsightService.logTrace(ClientTaxonomyEvents.EVENT_KO, [
    {
      origin: origin || '',
      credit_proposal_id: uuid || '',
      step_number: stepNumber || '',
    },
  ]);
  setClarityCustomTags({
    [ClientTaxonomyEvents.EVENT_KO]: origin || '',
    [ClarityTaxonomyProps.STEP_NUMBER]: stepNumber || '',
  });
};

export const registerLoanRefused = (
  generalData: TaxonomyEventGeneralProps,
  step: string,
) => {
  const { origin, uuid } = generalData;
  Analytics.registerAnalyticsEvent({
    [ClientTaxonomyEvents.LOAN_REFUSED]: {
      origin: origin || '',
      credit_proposal_id: uuid || '',
      denial_step: step || '',
    },
  });
};

// Eventos entry
export const registerSimulation = (
  generalData: TaxonomyEventGeneralProps,
  data: SimulationAnalyticsProps,
) => {
  const { origin } = generalData;
  Analytics.registerAnalyticsEvent({
    [ClientTaxonomyEvents.SIMULATION]: {
      origin,
      ...data,
    },
  });
  InsightService.logTrace(ClientTaxonomyEvents.SIMULATION, [data]);
};

export const registerSimulationEdit = (
  generalData: TaxonomyEventGeneralProps,
) => {
  const { origin } = generalData;
  Analytics.registerAnalyticsEvent({
    [ClientTaxonomyEvents.EDIT_SIMULATION]: {
      origin,
    },
  });
};

export const registerSimulationChange = (
  generalData: TaxonomyEventGeneralProps,
) => {
  const { origin } = generalData;
  Analytics.registerAnalyticsEvent({
    [ClientTaxonomyEvents.CHANGE_SIMULATION]: {
      origin,
    },
  });
};

export const registerEntryContinue = (
  generalData: TaxonomyEventGeneralProps,
  data: EntryContinueAnalytics,
) => {
  const { origin } = generalData;
  const eventBody = {
    origin,
    ...data,
  };
  Analytics.registerAnalyticsEvent({
    [ClientTaxonomyEvents.CONTINUE_ENTRY]: eventBody,
  });
  InsightService.logTrace(ClientTaxonomyEvents.CONTINUE_ENTRY, [eventBody]);
  setClarityCustomTags({
    [ClientTaxonomyEvents.CONTINUE_ENTRY]: 'done',
    [ClientTaxonomyEvents.ORIGIN]: origin,
    [ClarityTaxonomyProps.SIMULATION_AMOUNT]: data?.simulation_amount
      ? data.simulation_amount.toString()
      : '',
    [ClarityTaxonomyProps.SIMULATION_TERM]: data?.simulation_term
      ? data.simulation_term.toString()
      : '',
    [ClarityTaxonomyProps.SIMULATION_TAE]: data?.simulation_tae
      ? data.simulation_tae.toString()
      : '',
    [ClarityTaxonomyProps.SIMULATION_TIN]: data?.simulation_tin
      ? data.simulation_tin.toString()
      : '',
    [ClarityTaxonomyProps.SIMULATION_REGULAR_FEE]: data?.simulation_regular_fee
      ? data.simulation_regular_fee.toString()
      : '',
    [ClarityTaxonomyProps.SIMULATION_FIRST_FEE]: data.simulation_first_fee
      ? data.simulation_first_fee.toString()
      : '',
    [ClarityTaxonomyProps.SIMULATION_TOTAL_COST]: data.simulation_total_cost
      ? data.simulation_total_cost.toString()
      : '',
    [ClarityTaxonomyProps.SIMULATION_OPENING_FEE]: data.simulation_opening_fee
      ? data.simulation_opening_fee.toString()
      : '',
    [ClarityTaxonomyProps.SIMULATION_OPENING_FEE_TYPE]:
      data.simulation_opening_fee_type ? data.simulation_opening_fee_type : '',
    [ClarityTaxonomyProps.SIMULATION_FLOW]: data.simulation_flow
      ? data.simulation_flow
      : '',
    [ClarityTaxonomyProps.STEP_NUMBER]: data.step_number
      ? data.step_number.toString()
      : '',
  });
};

export const registerBeginTransaction = (
  generalData: TaxonomyEventGeneralProps,
  data: CheckoutAnalyticsProps,
) => {
  const { origin } = generalData;
  Analytics.registerAnalyticsEvent({
    [ClientTaxonomyEvents.BEGIN_TRANSACTION]: {
      origin,
      ...data,
    },
  });
};

// Eventos NFC
export const registerNFCSuccess = (
  generalData: TaxonomyEventGeneralProps,
  data: NfcSuccessAnalytics,
) => {
  const { origin } = generalData;
  Analytics.registerAnalyticsEvent({
    [ClientTaxonomyEvents.READ_NFCID_SUCCESS]: {
      origin,
      ...data,
    },
  });
  InsightService.logTrace(ClientTaxonomyEvents.READ_NFCID_SUCCESS, [
    origin,
    data,
  ]);
  setClarityCustomTags({
    [ClientTaxonomyEvents.READ_NFCID_SUCCESS]: 'done',
    [ClientTaxonomyEvents.ORIGIN]: origin,
    [ClarityTaxonomyProps.TIME_READING]: data.time_reading
      ? data.time_reading.toString()
      : '',
    [ClarityTaxonomyProps.TIME_ELAPSED]: data.time_elapsed
      ? data.time_elapsed.toString()
      : '',
    [ClarityTaxonomyProps.DOCUMENT_TYPE]: data?.document_type || '',
    [ClarityTaxonomyProps.STEP_NUMBER]: data.step_number.toString() || '',
  });
};

export const registerNFCError = (
  generalData: TaxonomyEventGeneralProps,
  time: number,
) => {
  const { origin } = generalData;
  Analytics.registerAnalyticsEvent({
    [ClientTaxonomyEvents.READ_NFCID_ERROR]: {
      origin,
      time_reading: time || 0,
    },
  });
  InsightService.logTrace(ClientTaxonomyEvents.READ_NFCID_ERROR, [
    origin,
    time,
  ]);
  setClarityCustomTags({
    [ClientTaxonomyEvents.READ_NFCID_ERROR]: 'done',
    [ClientTaxonomyEvents.ORIGIN]: origin || '',
    [ClarityTaxonomyProps.TIME_READING]: time ? time.toString() : '',
  });
};

export const registerNFCSkip = (
  generalData: TaxonomyEventGeneralProps,
  data: NfcSkipAnalytics,
) => {
  const { origin } = generalData;
  Analytics.registerAnalyticsEvent({
    [ClientTaxonomyEvents.SKIP_NFCID]: {
      origin,
      ...data,
    },
  });
  InsightService.logTrace(ClientTaxonomyEvents.SKIP_NFCID, [origin, data]);
};

// Eventos Datos Personales
export const registerEntryBack = (
  generalData: TaxonomyEventGeneralProps,
  time: number,
) => {
  const { origin } = generalData;
  Analytics.registerAnalyticsEvent({
    [ClientTaxonomyEvents.BACK_ENTRY]: {
      origin: origin || '',
      time_elapsed: time || 0,
    },
  });
};

export const registerBelenderUnavailable = (
  generalData: TaxonomyEventGeneralProps,
  type: string,
) => {
  const { origin } = generalData;
  const eventBody = {
    origin: origin || '',
    type: type || '',
  };
  Analytics.registerAnalyticsEvent({
    [ClientTaxonomyEvents.BELENDER_UNAVAILABLE]: eventBody,
  });
  InsightService.logTrace(ClientTaxonomyEvents.BELENDER_UNAVAILABLE, [
    eventBody,
  ]);
};

export const registerPersonalDataContinue = (
  generalData: TaxonomyEventGeneralProps,
  data: PersonalDataContinueAnalytics,
) => {
  const { origin, uuid } = generalData;
  const eventBody = {
    origin: origin || '',
    credit_proposal_id: uuid || '',
    ...data,
  };
  Analytics.registerAnalyticsEvent({
    [ClientTaxonomyEvents.CONTINUE_PDATA]: eventBody,
  });
  InsightService.logTrace(ClientTaxonomyEvents.CONTINUE_PDATA, [eventBody]);
  setClarityCustomTags({
    [ClientTaxonomyEvents.CONTINUE_PDATA]: 'done',
    [ClientTaxonomyEvents.ORIGIN]: origin || '',
    [ClarityTaxonomyProps.TIME_ELAPSED]: data?.time_elapsed
      ? data.time_elapsed.toString()
      : '',
    [ClarityTaxonomyProps.STEP_NUMBER]: data.step_number
      ? data.step_number.toString()
      : '',
    [ClarityTaxonomyProps.CUST_AGE_SEGMENT]: data.cust_age_segment || '',
    [ClarityTaxonomyProps.CUST_BIRTH_COUNTRY]: data.cust_birth_country || '',
    [ClarityTaxonomyProps.CUST_NATIONALITY]: data.cust_classification || '',
    [ClarityTaxonomyProps.CUST_EMAIL_DOMAIN]: data.cust_email_domain || '',
    [ClarityTaxonomyProps.CUST_CLASSIFICATION]: data.cust_classification || '',
    [ClarityTaxonomyProps.PROP_REQUIRE_DOCUMENTATION]:
      data.prop_require_documentation
        ? data.prop_require_documentation?.toString()
        : '',
    [ClarityTaxonomyProps.PROP_REQUIRE_LABOR_DATA]: data.prop_require_labor_data
      ? data.prop_require_labor_data?.toString()
      : '',
    [ClarityTaxonomyProps.PROP_REQUIRE_SECURE_PAYMENT]:
      data.prop_require_secure_payment
        ? data.prop_require_secure_payment?.toString()
        : '',
    [ClarityTaxonomyProps.PROP_REQUIRE_ADDRESS_DATA]:
      data.prop_require_address_data
        ? data.prop_require_address_data?.toString()
        : '',
    [ClarityTaxonomyProps.PROP_REQUIRE_ENRICHMENT_PERSONAL_FINANCES]:
      data.prop_require_enrichment_personal_finances
        ? data.prop_require_enrichment_personal_finances?.toString()
        : '',
    [ClarityTaxonomyProps.PROP_SOURCE_APPLICATION]:
      data.prop_source_application || '',
    [ClarityTaxonomyProps.PROP_PRODUCT]: data.prop_product
      ? data.prop_product?.toString()
      : '',
    [ClarityTaxonomyProps.PROP_AMOUNT]: data.prop_amount
      ? data.prop_amount?.toString()
      : '',
  });
};

// Eventos Datos Laborales
export const registerLaborDataContinue = (
  generalData: TaxonomyEventGeneralProps,
  data: LaborDataContinueAnalytics,
) => {
  const { origin, uuid } = generalData;
  const eventBody = {
    origin,
    credit_proposal_id: uuid,
    ...data,
  };
  Analytics.registerAnalyticsEvent({
    [ClientTaxonomyEvents.CONTINUE_LDATA]: eventBody,
  });
  InsightService.logTrace(ClientTaxonomyEvents.CONTINUE_LDATA, [eventBody]);
  setClarityCustomTags({
    [ClientTaxonomyEvents.CONTINUE_LDATA]: 'done',
    [ClientTaxonomyEvents.ORIGIN]: origin || '',
    [ClarityTaxonomyProps.TIME_ELAPSED]: data.time_elapsed
      ? data.time_elapsed.toString()
      : '',
    [ClarityTaxonomyProps.STEP_NUMBER]: data.step_number
      ? data.step_number.toString()
      : '',
    [ClarityTaxonomyProps.CUST_CONTRACT_TYPE]: data.cust_contract_type
      ? data.cust_contract_type.toString()
      : '',
    [ClarityTaxonomyProps.CUST_MONTHLY_INCOME]: data?.cust_monthly_income || '',
    [ClarityTaxonomyProps.CUST_PROVINCE]: data?.cust_province || '',
    [ClarityTaxonomyProps.CUST_POSTAL_CODE]: data?.cust_postal_code,
    [ClarityTaxonomyProps.USED_MANUAL_INPUT]: data.used_manual_input
      ? data.used_manual_input?.toString()
      : '',
  });
};

export const registerManualInput = (generalData: TaxonomyEventGeneralProps) => {
  const { origin, uuid } = generalData;
  const eventBody = {
    origin: origin || '',
    credit_proposal_id: uuid || '',
  };
  Analytics.registerAnalyticsEvent({
    [ClientTaxonomyEvents.MANUAL_INPUT_AVAILABLE]: eventBody,
  });
  InsightService.logTrace(ClientTaxonomyEvents.MANUAL_INPUT_AVAILABLE, [
    eventBody,
  ]);
};

export const registerShippingContinue = (
  generalData: TaxonomyEventGeneralProps,
  data: ShippingContinueAnalytics,
) => {
  const { origin, uuid } = generalData;
  const eventBody = {
    origin,
    credit_proposal_id: uuid,
    ...data,
  };
  Analytics.registerAnalyticsEvent({
    [ClientTaxonomyEvents.CONTINUE_SHIPPING]: eventBody,
  });
  InsightService.logTrace(ClientTaxonomyEvents.CONTINUE_SHIPPING, [eventBody]);
  setClarityCustomTags({
    [ClientTaxonomyEvents.CONTINUE_SHIPPING]: 'done',
    [ClientTaxonomyEvents.ORIGIN]: origin || '',
    [ClarityTaxonomyProps.TIME_ELAPSED]: data.time_elapsed
      ? data.time_elapsed.toString()
      : '',
    [ClarityTaxonomyProps.STEP_NUMBER]: data.step_number
      ? data.step_number.toString()
      : '',
    [ClarityTaxonomyProps.CONTINUE_MODE]: data.continue_mode || '',
  });
};

// Eventos TPV
export const registerPaymentInfo = (
  generalData: TaxonomyEventGeneralProps,
  data: CheckoutAnalyticsProps,
) => {
  const { origin, uuid } = generalData;
  const eventBody = {
    origin: origin || '',
    credit_proposal_id: uuid || '',
    ...data,
  };
  Analytics.registerAnalyticsEvent({
    [ClientTaxonomyEvents.ADD_PAYMENT_INFO]: eventBody,
  });
};

export const registerPaymentContinue = (
  generalData: TaxonomyEventGeneralProps,
  data: PaymentContinueAnalytics,
) => {
  const { origin, uuid } = generalData;
  const eventBody = {
    origin: origin || '',
    credit_proposal_id: uuid || '',
    ...data,
  };
  Analytics.registerAnalyticsEvent({
    [ClientTaxonomyEvents.CONTINUE_PAYMENT]: eventBody,
  });
  InsightService.logTrace(ClientTaxonomyEvents.CONTINUE_PAYMENT, [eventBody]);
  setClarityCustomTags({
    [ClientTaxonomyEvents.CONTINUE_PAYMENT]: 'done',
    [ClientTaxonomyEvents.ORIGIN]: origin || '',
    [ClarityTaxonomyProps.TIME_ELAPSED]: data.time_elapsed
      ? data.time_elapsed.toString()
      : '',
    [ClarityTaxonomyProps.STEP_NUMBER]: data.step_number
      ? data.step_number.toString()
      : '',
    [ClarityTaxonomyProps.PAYMENT_TYPE]: data.payment_type || '',
    [ClarityTaxonomyProps.HAS_PREVIOUS_CARDS]: data.has_previous_cards
      ? data.has_previous_cards?.toString()
      : '',
    [ClarityTaxonomyProps.AMOUNT]: data.amount ? data.amount.toString() : '',
    [ClarityTaxonomyProps.THIRD]: data.third || '',
  });
};

export const registerPaymentError = (
  generalData: TaxonomyEventGeneralProps,
  thirdParty: string,
) => {
  const { origin, uuid } = generalData;
  const eventBody = {
    origin: origin || '',
    credit_proposal_id: uuid || '',
    third: thirdParty || '',
  };
  Analytics.registerAnalyticsEvent({
    [ClientTaxonomyEvents.PAYMENT_ERROR]: eventBody,
  });
  InsightService.logTrace(ClientTaxonomyEvents.PAYMENT_ERROR, [eventBody]);
};

// Eventos firma
export const registerContractShown = (
  generalData: TaxonomyEventGeneralProps,
  thirdParty?: string,
) => {
  const { origin, uuid } = generalData;
  const eventBody = {
    origin: origin || '',
    credit_proposal_id: uuid || '',
    third: thirdParty || '',
  };
  Analytics.registerAnalyticsEvent({
    [ClientTaxonomyEvents.SHOW_CONTRACT]: eventBody,
  });
};

export const registerOtpResend = (
  generalData: TaxonomyEventGeneralProps,
  data: SignatureAnalyticsProps,
) => {
  const { origin, uuid } = generalData;
  const eventBody = {
    origin: origin || '',
    credit_proposal_id: uuid || '',
    ...data,
  };
  Analytics.registerAnalyticsEvent({
    [ClientTaxonomyEvents.RESEND_OTP]: eventBody,
  });
};

export const registerPinError = (
  generalData: TaxonomyEventGeneralProps,
  thirdParty?: string,
) => {
  const { origin, uuid } = generalData;
  const eventBody = {
    origin: origin || '',
    credit_proposal_id: uuid || '',
    third: thirdParty || '',
  };
  Analytics.registerAnalyticsEvent({
    [ClientTaxonomyEvents.PIN_ERROR]: eventBody,
  });
};

export const registerSignatureContinue = (
  generalData: TaxonomyEventGeneralProps,
  data: SignatureAnalyticsProps,
) => {
  const { origin, uuid } = generalData;
  const eventBody = {
    origin: origin || '',
    credit_proposal_id: uuid || '',
    ...data,
  };
  Analytics.registerAnalyticsEvent({
    [ClientTaxonomyEvents.CONTINUE_SIGNATURE]: eventBody,
  });
  InsightService.logTrace(ClientTaxonomyEvents.CONTINUE_SIGNATURE, [eventBody]);
  setClarityCustomTags({
    [ClientTaxonomyEvents.CONTINUE_SIGNATURE]: 'done',
    [ClientTaxonomyEvents.ORIGIN]: origin || '',
    [ClarityTaxonomyProps.TIME_ELAPSED]: data.time_elapsed
      ? data.time_elapsed.toString()
      : '',
    [ClarityTaxonomyProps.STEP_NUMBER]: data.step_number
      ? data.step_number?.toString()
      : '',
    [ClarityTaxonomyProps.THIRD]: data.third || '',
  });
};

// Eventos finales
export const registerVerify = (generalData: TaxonomyEventGeneralProps) => {
  Analytics.registerAnalyticsEvent({
    [ClientTaxonomyEvents.VERIFY]: generalData,
  });
  InsightService.logTrace(ClientTaxonomyEvents.VERIFY, [generalData]);
  setClarityCustomTags({
    [ClientTaxonomyEvents.VERIFY]: 'done',
    [ClientTaxonomyEvents.ORIGIN]: generalData.origin || '',
  });
};

export const registerPurchase = (
  generalData: TaxonomyEventGeneralProps,
  data: PurchaseAnalytics,
) => {
  const { origin, uuid } = generalData;
  const eventBody = {
    origin,
    credit_proposal_id: uuid,
    ...data,
  };
  Analytics.registerAnalyticsEvent({
    [ClientTaxonomyEvents.PURCHASE]: eventBody,
  });
  InsightService.logTrace(ClientTaxonomyEvents.PURCHASE, [generalData]);
  setClarityCustomTags({
    [ClientTaxonomyEvents.PURCHASE]: 'done',
    [ClientTaxonomyEvents.ORIGIN]: generalData?.origin || '',
    [ClarityTaxonomyProps.CURRENCY]: data?.currency || '',
    [ClarityTaxonomyProps.TRANSACTION_ID]: data?.transaction_id || '',
    [ClarityTaxonomyProps.VALUE]: data.value ? data.value.toString() : '',
    [ClarityTaxonomyProps.ITEM_ID]: data?.items?.item_id || '',
    [ClarityTaxonomyProps.ITEM_NAME]: data?.items?.item_name || '',
  });
};

export const registerLeadGenerated = (
  generalData: TaxonomyEventGeneralProps,
  data: GenerateLeadAnalytics,
) => {
  const { origin, uuid } = generalData;
  const eventBody = {
    origin,
    credit_proposal_id: uuid,
    ...data,
  };
  Analytics.registerAnalyticsEvent({
    [ClientTaxonomyEvents.GENERATE_LEAD]: eventBody,
  });
};

// Eventos banner de notificaciones (dashboard de operaciones)
export const registerShowNotificationBanner = (
  data: ShowNotificationBannerAnalytics,
) => {
  Analytics.registerAnalyticsEvent({
    [ClientTaxonomyEvents.SHOW_NOTIFICATION_BANNER]: data,
  });
};

export const registerClickNotificationBannerCta = () => {
  Analytics.registerAnalyticsEvent({
    [ClientTaxonomyEvents.CLICK_NOTIFICATION_BANNER_CTA]: {},
  });
};

export const registerCloseNotificationBanner = () => {
  Analytics.registerAnalyticsEvent({
    [ClientTaxonomyEvents.CLOSE_NOTIFICATION_BANNER]: {},
  });
};
